import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { targetsListQueryOptions } from "@/lib/targets";
import { TargetBadge } from "@/components/targets/TargetBadge";
import { type TargetStatus } from "@/lib/targets-schemas";
import { formatUsd, formatRelativeTime, formatPhoneUs } from "@/lib/format";
import { useTopbar } from "@/components/chrome/use-topbar";

export const Route = createFileRoute("/_app/targets/")({
  component: TargetsListPage,
});

type ChipFilter = "all" | TargetStatus;
const CHIPS: { id: ChipFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "pursuing", label: "Pursuing" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
  { id: "passed", label: "Passed" },
];

type SortKey = "delta" | "loans" | "lastSeen" | "borrower";

function targetHref(slug: string): string {
  return `/targets/${encodeURIComponent(slug)}`;
}

function TargetsListPage() {
  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({ crumbs: [{ label: "Operations" }, { label: "Targets" }], rightSlot: null });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  // Always fetch ALL targets and filter client-side so chip counts are live.
  const { data, isLoading } = useQuery(targetsListQueryOptions(null));
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const [filter, setFilter] = useState<ChipFilter>("pursuing");
  const [sortKey, setSortKey] = useState<SortKey>("delta");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const counts: Record<ChipFilter, number> = useMemo(() => {
    const base: Record<ChipFilter, number> = {
      all: rows.length,
      pursuing: 0,
      won: 0,
      lost: 0,
      passed: 0,
    };
    for (const r of rows) if (r.pursuitStatus) base[r.pursuitStatus] += 1;
    return base;
  }, [rows]);

  const filtered = useMemo(() => {
    const f = filter === "all" ? rows : rows.filter((r) => r.pursuitStatus === filter);
    const sorted = [...f].sort((a, b) => {
      let cmp: number;
      switch (sortKey) {
        case "delta":
          cmp = a.totalDeltaUsd - b.totalDeltaUsd;
          break;
        case "loans":
          cmp = a.loanCount - b.loanCount;
          break;
        case "lastSeen":
          cmp = (a.lastSeenAt ?? 0) - (b.lastSeenAt ?? 0);
          break;
        case "borrower":
          cmp = a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [rows, filter, sortKey, sortDir]);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;

  return (
    <div className="space-y-5">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-0.5 rounded-lg bg-paper-2 p-0.5 md:inline-flex">
        {CHIPS.map((c) => {
          const active = filter === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setFilter(active && c.id !== "all" ? "all" : c.id)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-semibold transition ${
                active ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink"
              }`}
            >
              {c.label}
              <span className="rounded-full bg-paper-3 px-1.5 py-0.5 text-[10px] font-bold text-muted">
                {counts[c.id]}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-rule bg-surface px-8 py-10 text-center text-sm text-muted">
          No targets in this view. Add a borrower from any loan's Borrower card.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-md border border-rule bg-surface md:block">
            <table className="w-full border-collapse text-sm">
              <thead className="border-b border-rule bg-paper text-left">
                <tr className="text-[10px] uppercase tracking-wider text-muted">
                  <th className="px-4 py-3">Borrower</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4 text-right">Loans</th>
                  <th className="py-3 pr-4 text-right">Total Δ</th>
                  <th className="py-3 pr-4">Contact</th>
                  <th className="py-3 pr-4">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.normalizedKey}
                    className="cursor-pointer border-b border-rule transition hover:bg-paper"
                    onClick={() => {
                      window.location.href = targetHref(r.slug);
                    }}
                  >
                    <td className="px-4 py-3.5">
                      <span className="block font-semibold text-ink">{r.displayName}</span>
                      {r.principalName && (
                        <span className="mt-0.5 block text-[11px] text-muted-2">
                          {r.principalName} · principal
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 pr-4">
                      <TargetBadge status={r.pursuitStatus} />
                    </td>
                    <td className="py-3.5 pr-4 text-right tabular-nums">{r.loanCount}</td>
                    <td className="py-3.5 pr-4 text-right font-semibold tabular-nums text-pos">
                      {r.totalDeltaUsd > 0
                        ? `+${formatUsd(r.totalDeltaUsd)}`
                        : formatUsd(r.totalDeltaUsd)}
                    </td>
                    <td className="py-3.5 pr-4 text-[12px] text-muted">
                      {formatPhoneUs(r.primaryPhone) ?? r.primaryEmail ?? "—"}
                    </td>
                    <td className="py-3.5 pr-4 text-[12px] text-muted">
                      {r.lastSeenAt ? formatRelativeTime(r.lastSeenAt) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: sort + cards */}
          <div className="md:hidden">
            <div className="mb-3 flex items-center gap-2">
              <label
                htmlFor="targets-sort"
                className="text-[11px] font-semibold uppercase tracking-wider text-muted"
              >
                Sort
              </label>
              <select
                id="targets-sort"
                value={sortKey}
                onChange={(e) => {
                  setSortKey(e.target.value as SortKey);
                  setSortDir("desc");
                }}
                className="flex-1 rounded-md border border-rule bg-surface px-3 py-2 text-sm"
              >
                <option value="delta">Total Δ</option>
                <option value="loans">Loans</option>
                <option value="lastSeen">Last seen</option>
                <option value="borrower">Borrower</option>
              </select>
              <button
                type="button"
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                aria-label="Toggle sort direction"
                className="grid h-10 w-10 place-items-center rounded-md border border-rule bg-surface text-ink"
              >
                {sortDir === "asc" ? "↑" : "↓"}
              </button>
            </div>
            <ul className="flex flex-col gap-3">
              {filtered.map((r) => (
                <li key={r.normalizedKey} className="rounded-lg border border-rule bg-surface p-4">
                  <a href={targetHref(r.slug)} className="block">
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <span className="font-semibold text-ink">{r.displayName}</span>
                      <TargetBadge status={r.pursuitStatus} />
                    </div>
                    <div className="mb-2 text-[12px] text-muted-2">
                      {r.loanCount} loans · {r.totalDeltaUsd > 0 ? "+" : ""}
                      {formatUsd(r.totalDeltaUsd)} total Δ
                    </div>
                    <div className="text-[12px] text-muted">
                      {formatPhoneUs(r.primaryPhone) ?? r.primaryEmail ?? "No contact"}
                      {" · "}
                      {r.states.join(", ") || "—"}
                      {" · "}
                      {r.lastSeenAt ? formatRelativeTime(r.lastSeenAt) : "—"}
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
